class DeliveryChecker extends HTMLElement {
  constructor() {
    super();
    this.checkDelivery = this.checkDelivery.bind(this);
    this.handleChangeClick = this.handleChangeClick.bind(this);
    this.storedData = this.getSessionStorage("shippingRates");

    this.checkExistingData();
  }

  connectedCallback() {
    this.populateProvinceOptions();
    this.form.addEventListener("submit", this.checkDelivery);
    this.changeButton?.addEventListener("click", this.handleChangeClick);
  }

  disconnectedCallback() {
    this.form.removeEventListener("submit", this.checkDelivery);
    this.changeButton?.removeEventListener("click", this.handleChangeClick);
  }

  async checkDelivery(event) {
    event.preventDefault();

    const pinCode = this.input.value.trim();
    let lineItemKey = null;
    let addedItem = null;

    if (!pinCode) {
      this.displayMessage(window.generalStrings.pinCodeError, { isError: true });
      return;
    }

    this.button.disabled = true;
    this.displayMessage(window.generalStrings.checkingDelivery);

    const sourceUrl = this.sourceUrl;
    let shiprocketData = null;

    if (sourceUrl) {
      try {
        const shiprocketParams = new URLSearchParams();
        shiprocketParams.set("variantId", this.variantId);
        shiprocketParams.set("pin", pinCode);
        const shiprocketResponse = await fetch(`${sourceUrl}?${shiprocketParams}`);
        shiprocketData = await shiprocketResponse.json();
        console.log(shiprocketData);
      } catch (err) {
        console.error(err);
      }
    }

    try {
      let ratesHtml;

      if (this.useShiprocket) {
        ratesHtml = this.buildShiprocketRates(shiprocketData);
      } else {
        try {
          addedItem = await addToCart(
            [
              {
                id: this.variantId,
                quantity: 1,
                properties: { _shipping_estimate: true },
              },
            ],
            false,
          );
        } catch (error) {
          this.displayMessage(window.generalStrings.productDeliveryError, {
            isError: true,
          });
          return;
        }

        if (!addedItem?.items?.[0]?.key) {
          this.displayMessage(window.generalStrings.productDeliveryError, {
            isError: true,
          });
          return;
        }

        lineItemKey = addedItem.items[0].key;

        const params = new URLSearchParams();
        params.set("shipping_address[zip]", pinCode);
        params.set("shipping_address[country]", this.country);

        const province = this.provinceSelect?.value?.trim() || "";
        if (province) {
          params.set("shipping_address[province]", province);
        }

        const response = await fetch(`/cart/shipping_rates.json?${params}`);
        const data = await this.parseJsonResponse(response);

        if (!response.ok) {
          throw new Error(
            this.extractErrorMessage(data) || window.generalStrings.noDeliveryOptions,
          );
        }

        if (data?.error) {
          throw new Error(this.extractErrorMessage(data));
        }

        ratesHtml = this.buildShopifyRates(data);
      }

      if (!ratesHtml) {
        throw new Error(window.generalStrings.noDeliveryOptions);
      }

      const successHtml = this.buildSuccessMessage(pinCode, ratesHtml);
      this.setSessionStorage("shippingRates", successHtml);
      this.displayMessage(successHtml, { allowHtml: true });
      this.setFieldsHidden(true);

    } catch (error) {
      console.error(error);
      this.displayMessage(
        this.extractErrorMessage(error) || window.generalStrings.noDeliveryOptions,
        { isError: true },
      );
    } finally {
      if (lineItemKey) {
        try {
          await updateCartItem(lineItemKey, 0);
        } catch (cleanupError) {
          console.error(cleanupError);
        }
      }
      this.button.disabled = false;
    }
  }

  buildShiprocketRates(data) {
    if (!data?.estimate) return null;
    return `<strong>${window.generalStrings.estimatedDelivery}</strong> ${data.estimate}`;
  }

  buildShopifyRates(data) {
    if (!data?.shipping_rates?.length) return null;
    return data.shipping_rates
      .map((rate) =>
        rate.description
          ? `<strong>${rate.name}</strong> (${rate.description}): ${this.formatPrice(rate.price)}`
          : `<strong>${rate.name}</strong>: ${this.formatPrice(rate.price)}`,
      )
      .join("<br>");
  }

  checkExistingData() {
    if (this.storedData) {
      this.displayMessage(this.storedData, { allowHtml: true });
      this.setFieldsHidden(true);
    }
  }

  handleChangeClick() {
    this.removeSessionStorage("shippingRates");
    this.storedData = null;
    this.displayMessage("");
    this.setFieldsHidden(false);
    this.input?.focus();
  }

  setSessionStorage(key, value) {
    sessionStorage.setItem(key, value);
  }

  getSessionStorage(key) {
    return sessionStorage.getItem(key);
  }

  removeSessionStorage(key) {
    sessionStorage.removeItem(key);
  }

  displayMessage(message, { allowHtml = false, isError = false } = {}) {
    this.messageContainer.classList.toggle(
      "delivery-checker__message--error",
      Boolean(isError),
    );

    if (allowHtml) {
      this.messageContainer.innerHTML = message;
      return;
    }

    this.messageContainer.textContent = message;
  }

  async parseJsonResponse(response) {
    try {
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  extractErrorMessage(error) {
    if (!error) {
      return "";
    }

    if (typeof error === "string") {
      return error;
    }

    if (Array.isArray(error)) {
      const arrayMessage = error
        .map((item) => this.extractErrorMessage(item))
        .find((message) => Boolean(message));

      return arrayMessage || "";
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    if (Array.isArray(error.errors) && error.errors.length) {
      return this.extractErrorMessage(error.errors);
    }

    if (typeof error.errors === "string") {
      return error.errors;
    }

    if (typeof error.error === "string") {
      return error.error;
    }

    if (typeof error.message === "string") {
      return error.message;
    }

    if (typeof error === "object") {
      const objectMessage = Object.values(error)
        .map((value) => this.extractErrorMessage(value))
        .find((message) => Boolean(message));

      return objectMessage || "";
    }

    return "";
  }

  buildSuccessMessage(pinCode, ratesHtml) {
    const heading = `${window.generalStrings.deliveryOptionsFor} ${pinCode}`;
    return `<strong class="delivery-checker__message-heading">${heading}</strong>${ratesHtml}`;
  }

  setFieldsHidden(isHidden) {
    const hideValue = Boolean(isHidden);

    if (this.label) {
      this.label.hidden = hideValue;
    }

    if (this.provinceSelect) {
      this.provinceSelect.hidden = hideValue;
    }

    if (this.inputGroup) {
      this.inputGroup.hidden = hideValue;
    }

    if (this.changeButton) {
      this.changeButton.classList.toggle("hidden", !hideValue);
    }
  }

  formatPrice(price) {
    let moneyFormat = window.moneyFormat;
    moneyFormat = moneyFormat.replace("Rs. ", "₹");

    if (price == 0.0) {
      return "Free";
    }

    return moneyFormat.replace("{{amount}}", price);
  }

  populateProvinceOptions() {
    if (!this.provinceSelect) {
      return;
    }

    if (this.useShiprocket) {
      this.provinceSelect.style.display = "none";
      return;
    }

    const provinces = this.provinces;

    if (!provinces.length) {
      this.provinceSelect.style.display = "none";
      return;
    }

    this.provinceSelect.innerHTML = "";

    const placeholderOption = document.createElement("option");
    placeholderOption.value = "";
    placeholderOption.textContent = window.generalStrings.selectStateProvince;
    this.provinceSelect.appendChild(placeholderOption);

    provinces.forEach((province) => {
      const option = document.createElement("option");
      option.value = province.code;
      option.textContent = province.name;
      this.provinceSelect.appendChild(option);
    });
  }

  getSelectedCountryOption() {
    if (!this.countryOptionsSelect) {
      return null;
    }

    const options = Array.from(this.countryOptionsSelect.options);

    return (
      options.find((option) => option.dataset.code === this.countryCode) ||
      options.find((option) => option.value === this.country) ||
      null
    );
  }

  get form() {
    return this.querySelector("form");
  }

  get input() {
    return this.querySelector("input");
  }

  get button() {
    return this.querySelector("button");
  }

  get changeButton() {
    return this.querySelector("[data-delivery-change]");
  }

  get provinceSelect() {
    return this.querySelector('select[name="province"]');
  }

  get label() {
    return this.querySelector(".delivery-checker__label");
  }

  get inputGroup() {
    return this.querySelector(".delivery-checker__input-group");
  }

  get countryOptionsSelect() {
    return this.querySelector("select[data-country-options]");
  }

  get messageContainer() {
    return this.querySelector("[data-form-message]");
  }

  get variantId() {
    return this.dataset.variantId;
  }

  get sourceUrl() {
    return this.dataset.sourceUrl || null;
  }

  get useShiprocket() {
    return this.dataset.useShiprocket === "true";
  }

  get country() {
    return this.dataset.country;
  }

  get countryCode() {
    return this.dataset.countryCode;
  }

  get provinces() {
    const selectedCountryOption = this.getSelectedCountryOption();

    if (!selectedCountryOption) {
      return [];
    }

    try {
      const rawProvinces = JSON.parse(
        selectedCountryOption.dataset.provinces || "[]",
      );

      return rawProvinces
        .map((province) => {
          if (Array.isArray(province)) {
            return {
              code: province[0] || province[1] || "",
              name: province[1] || province[0] || "",
            };
          }

          if (province && typeof province === "object") {
            return {
              code: province.code || province.name || "",
              name: province.name || province.code || "",
            };
          }

          return null;
        })
        .filter((province) => province && province.name);
    } catch (error) {
      return [];
    }
  }
}

customElements.define("delivery-checker", DeliveryChecker);